/**
 * PvP Roulette — Frontend Contract Types
 *
 * Transport: Centrifugo
 * Channel: realtime-events:games:pvp-roulette:room:{room_name}
 * Event: { type: 'RoundUpdated', server_time: number (Unix ms), round: RoundDTO }
 * Timestamps: Unix milliseconds (ms) — compatible with Date.now() directly
 * Durations: seconds (bettingDurationSeconds etc.)
 * Coordinates: 0..99999 (100000 discrete positions → full circle)
 *
 * Version guard: ignore snapshot if version <= current.
 * Tricks: only in TRICKS phase; validate via allowed_tricks.
 * Bootstrap: GetPvpRouletteRound + Centrifugo subscribe (parallel); version guard
 */

// =============================================================================
// Constants
// =============================================================================

export const COORDINATE_MAX_BACKEND = 100000
export const COORDINATE_MAX_UI = 360
export const TRICK_TYPE_PUSH_WHEEL = "PUSH_WHEEL"
export const TRICK_TYPE_FREEZE_WHEEL = "FREEZE_WHEEL"

// =============================================================================
// Round Status
// =============================================================================

export type RoundStatus =
  | "WAITING"
  | "BETTING"
  | "SPINNING"
  | "TRICKS"
  | "SLOWING"
  | "RESOLVED"

// =============================================================================
// Other Enums
// =============================================================================

export type GameMode = "SOLO" | "PVP"

export type SnapshotBetStatus =
  | "PENDING"
  | "ACTIVE"
  | "WON"
  | "LOST"
  | "CANCELED"

/** Bets that participate in the round (excludes CANCELED). */
export function getActiveBets<T extends { status?: SnapshotBetStatus }>(
  bets: T[]
): T[] {
  return bets.filter((b) => b.status === "ACTIVE" || b.status === "PENDING")
}

export type TrickType =
  | typeof TRICK_TYPE_PUSH_WHEEL
  | typeof TRICK_TYPE_FREEZE_WHEEL
  | (string & {})

// =============================================================================
// Coordinates
// =============================================================================

export type CoordinateCentidegrees = number
export type CoordinateDegrees = number

export function centidegreesToDegrees(
  centideg: CoordinateCentidegrees
): CoordinateDegrees {
  return (centideg / COORDINATE_MAX_BACKEND) * COORDINATE_MAX_UI
}

export function degreesToCentidegrees(
  deg: CoordinateDegrees
): CoordinateCentidegrees {
  return Math.round((deg / COORDINATE_MAX_UI) * COORDINATE_MAX_BACKEND)
}

// =============================================================================
// Parties
// =============================================================================

export interface RoundParty {
  id?: string
  userId: string
  username?: string
  betAmount: string
  percent: string
  sectorId?: string
  sectorFrom?: number
  sectorTo?: number
  color?: number
  partyId?: string
  partyType?: "SOLO" | "TEAM"
  totalBank?: number
  winProbability?: number
  playerBanks?: Array<{
    userData: BetUserData
    bank: number
    partyShare?: number
    winProbability?: number
    potentialGrossWinAmount?: number
    potentialGrossPayoutAmount?: number
    potentialNetWinAmount?: number
    potentialNetPayoutAmount?: number
    grossWinAmount?: number
    grossPayoutAmount?: number
    netWinAmount?: number
    netPayoutAmount?: number
  }>
  potentialGrossWinAmount?: number
  potentialGrossPayoutAmount?: number
  potentialNetWinAmount?: number
  potentialNetPayoutAmount?: number
  grossWinAmount?: number
  grossPayoutAmount?: number
  netWinAmount?: number
  netPayoutAmount?: number
}

// =============================================================================
// Bets
// =============================================================================

/** User identity on a bet (from backend user_data; id may also be inferred from root user_id when mapping). */
export interface BetUserData {
  userId: string
  username: string | null
  photoUrl: string | null
}

export interface SnapshotBet {
  betId: string
  userData: BetUserData
  amount: string
  currency: "ton" | "stars"
  status: SnapshotBetStatus
  sectorId?: string
  groupId?: string
  partyId?: string
  partyType?: "SOLO" | "TEAM"
  tonEquivNano?: number
  assetClass?: "TON" | "TELEGRAM_GIFT"
  nftId?: string | null
  /** Fragment small-image URL for TELEGRAM_GIFT bets; absent for TON bets. */
  smallImageUrl?: string | null
  /** Unix ms */
  createdAt?: number | null
  /** Unix ms */
  terminatedAt?: number | null
}

// =============================================================================
// Tricks (backend: { type, user_id })
// =============================================================================

export interface AppliedTrick {
  type: TrickType
  userId: string
  /**
   * When set, round diff treats each entry as distinct even if `type`+`userId` repeat
   * (dev emulator / stress tests). Omitted in production API payloads.
   */
  instanceId?: string
}

// =============================================================================
// Timing
// =============================================================================

/** All timestamp fields are Unix milliseconds (ms), compatible with Date.now(). */
export interface RoundTiming {
  /** Unix ms */
  bettingStartedAt?: number | null
  /** Unix ms */
  bettingEndsAt?: number | null
  /** Unix ms */
  spinningStartedAt?: number | null
  /** Unix ms */
  spinningEndsAt?: number | null
  /** Unix ms */
  tricksStartedAt?: number | null
  /** Unix ms */
  tricksEndsAt?: number | null
  /** Unix ms */
  slowingStartedAt?: number | null
  /** Unix ms */
  slowingEndsAt?: number | null
}

// =============================================================================
// Settlement (RESOLVED payouts to winners; stake return not included)
// =============================================================================

export interface SettlementGift {
  nftId: string
  /** Gift TON-equivalent value (nanotons). */
  tonEquivNano: number
  collection?: string
  model?: string
  smallImageUrl?: string | null
  largeImageUrl?: string | null
}

export interface SettlementWinner {
  userId: string
  /** Prize Telegram gifts taken from losers' bank (new gifts, not stake return). */
  wonGifts: SettlementGift[]
  /** Prize TON cash before rake, without gifts and stake return (nanotons). */
  grossWinNano: number
  /** Prize TON cash after rake + platform compensation (nanotons). */
  netWinNano: number
}

export interface RoundSettlement {
  winners: SettlementWinner[]
}

// =============================================================================
// Round Snapshot
// =============================================================================

export interface RoundSnapshot {
  version: number
  roundId: string
  roundNum: number
  status: RoundStatus
  mode: GameMode

  parties: RoundParty[]
  bets: SnapshotBet[]
  tricks: AppliedTrick[]
  timing: RoundTiming

  targetCoordinate: number
  currentCoordinate?: number
  prizePoolTon: string

  roomName?: string
  bettingDurationSeconds?: number
  spinningDurationSeconds?: number
  tricksDurationSeconds?: number
  minTeamBet?: number
  minSoloBet?: number
  allowedTricks: string[]
  allowedTeams?: string[]

  /** Unix ms */
  bettingStartedAt?: number | null
  /** Unix ms */
  bettingEndsAt?: number | null
  /** Unix ms */
  spinningStartedAt?: number | null
  /** Unix ms */
  spinningEndsAt?: number | null
  /** Unix ms */
  tricksStartedAt?: number | null
  /** Unix ms */
  tricksEndsAt?: number | null
  /** Unix ms */
  slowingStartedAt?: number | null
  /** Unix ms */
  slowingEndsAt?: number | null
  /** Unix ms */
  createdAt?: number | null

  /** From room_config.slowing_offset_ms — ms before slowing_ends_at when wheel should visually stop. Already in ms, unchanged. */
  slowingOffsetMs?: number

  commitHash?: string
  clientCommitHash?: string
  /** SHA256(salt) as base64; present from round start (commit-reveal). */
  saltHash?: string
  salt?: string

  winnerPartyId?: string
  winnerPartyType?: "SOLO" | "TEAM"
  roulettePosition?: number

  /** Payout breakdown to winners; present at status=RESOLVED when saved. */
  settlement?: RoundSettlement | null
}

// =============================================================================
// Event
// =============================================================================

export interface RoundUpdatedEvent {
  type: "RoundUpdated"
  /** Unix ms on the server at push time. Use for clock-drift compensation. */
  server_time?: number
  round: Record<string, unknown>
}

// =============================================================================
// Client-only
// =============================================================================

export interface PendingBet {
  correlationId: string
  gameId: string
  amount: number
  currency: "ton" | "stars"
  submittedAt: number
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Backend may send Unix seconds or milliseconds. Values below ~1e12 are treated as seconds
 * so comparisons with Date.now() stay correct (fixes TRICKS → SLOWING never firing).
 */
export function normalizeUnixTimestampToMs(ts: number): number {
  if (!Number.isFinite(ts) || ts <= 0) return ts
  if (ts < 1e12) return Math.round(ts * 1000)
  return ts
}

/** Final wheel position. In SLOWING / RESOLVED, roulette_position is canonical when set. */
export function getEffectiveTargetCoordinate(
  snap: RoundSnapshot
): CoordinateCentidegrees {
  if (
    (snap.status === "RESOLVED" || snap.status === "SLOWING") &&
    snap.roulettePosition != null
  ) {
    return snap.roulettePosition
  }
  return snap.targetCoordinate ?? snap.roulettePosition ?? 0
}

/** Timing fields. BETTING uses betting_ends_at; SPINNING spinning_ends_at; TRICKS tricks_ends_at; SLOWING slowing_ends_at. */
export function getEffectiveTiming(snap: RoundSnapshot): RoundTiming {
  const t = snap.timing ?? {}
  return {
    ...t,
    bettingStartedAt: t.bettingStartedAt ?? snap.bettingStartedAt,
    bettingEndsAt: t.bettingEndsAt ?? snap.bettingEndsAt,
    spinningStartedAt: t.spinningStartedAt ?? snap.spinningStartedAt,
    spinningEndsAt: t.spinningEndsAt ?? snap.spinningEndsAt,
    tricksStartedAt: t.tricksStartedAt ?? snap.tricksStartedAt,
    tricksEndsAt: t.tricksEndsAt ?? snap.tricksEndsAt,
    slowingStartedAt: t.slowingStartedAt ?? snap.slowingStartedAt,
    slowingEndsAt: t.slowingEndsAt ?? snap.slowingEndsAt,
  }
}
